import { FC, ReactNode, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import CN from 'classnames'
import { useExportSsccXmlAll } from 'framework/api/methods/ssccXml/useGetSsccXmlAllList'

import { runExportWithToast } from 'utils/exportToast'

import { MANAGE_ORDERS_URL } from '../ManageOrdersLanding'

import { FixSsccXmlModal } from './FixSsccXml/FixSsccXmlModal'
import { SsccXmlAllTab } from './SsccXmlAllTab'
import { SsccXmlRetriggeredTab } from './SsccXmlRetriggeredTab'
import {
  SsccXmlValidationRow,
  SsccXmlValidationTab,
} from './SsccXmlValidationTab'

import 'react-toastify/dist/ReactToastify.css'

export interface SsccXmlValidationPageProps {
  [x: string]: any
}

type TabKey = 'validation' | 'retriggered' | 'all'

interface TabDefinition {
  key: TabKey
  label: string
  enabled: boolean
}

const tabs: TabDefinition[] = [
  { key: 'validation', label: 'SSCC XML Validation', enabled: true },
  { key: 'retriggered', label: 'Retriggered SSCC XML', enabled: true },
  { key: 'all', label: 'All SSCC XML', enabled: true },
]

/**
 * BDL20-4066: Manage Orders > SSCC XML Validation. Breadcrumb, Back, the three tabs (the
 * Retriggered tab is BDL20-3999, the All tab with its date range and CSV is BDL20-4003), the shared search box and the
 * Fix SSCC XML pop-up (BDL20-3995).
 */
export const SsccXmlValidationPage: FC<SsccXmlValidationPageProps> = ({
  className,
}: SsccXmlValidationPageProps) => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabKey>('validation')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [pendingCount, setPendingCount] = useState<number | null>(null)
  const [fixTarget, setFixTarget] = useState<SsccXmlValidationRow | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const { mutateAsync: exportAll } = useExportSsccXmlAll()

  // yyyy-MM-dd strings compare correctly as text.
  const rangeIsValid = !dateFrom || !dateTo || dateFrom <= dateTo

  const downloadCsv = () => {
    if (!rangeIsValid) return
    runExportWithToast({
      defaultFilename: 'All_SSCC_XML.csv',
      trigger: ({ onSuccess, onError }) => {
        exportAll({ search, from: dateFrom, to: dateTo })
          .then(onSuccess)
          .catch(onError)
      },
    })
  }

  // Search as the user types, without a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const switchTab = (key: TabKey) => {
    setActiveTab(key)
    setSearchInput('')
    setSearch('')
  }

  let content: ReactNode = null
  if (activeTab === 'validation') {
    content = (
      <SsccXmlValidationTab
        search={search}
        onPendingCountChange={setPendingCount}
        onFix={setFixTarget}
        refreshKey={refreshKey}
      />
    )
  }
  if (activeTab === 'all') {
    content = (
      <SsccXmlAllTab
        search={search}
        from={dateFrom}
        to={dateTo}
        rangeIsValid={rangeIsValid}
        refreshKey={refreshKey}
      />
    )
  }
  if (activeTab === 'retriggered') {
    content = <SsccXmlRetriggeredTab search={search} refreshKey={refreshKey} />
  }

  return (
    <div
      className={CN(
        'flex justify-center bg-N-50 h-full w-full px-8 py-5',
        className,
      )}>
      <div className='flex flex-col gap-4 h-full w-full rounded-xl bg-white shadow-sm px-6 pt-5 pb-[18px] overflow-auto'>
        <div className='flex items-center gap-[14px]'>
          <button
            type='button'
            onClick={() => navigate(MANAGE_ORDERS_URL)}
            className='inline-flex items-center gap-1 text-sm font-extrabold text-[#C47A2C] hover:text-[#A8631C]'>
            <i className='ri-arrow-left-s-line text-base' />
            Back
          </button>
          <span className='w-px h-4 bg-[#DADBDF]' />
          <nav
            aria-label='Breadcrumb'
            className='flex items-center gap-2 text-sm text-[#6B6F78]'>
            <button
              type='button'
              onClick={() => navigate(MANAGE_ORDERS_URL)}
              className='hover:text-[#C47A2C]'>
              Manage Orders
            </button>
            <i className='ri-arrow-right-s-line' />
            <span className='text-[#2A2A2D] font-extrabold'>
              SSCC XML Validation
            </span>
          </nav>
        </div>

        <div
          role='tablist'
          className='flex items-end gap-1 border-b border-[#E4E5E8]'>
          {tabs.map((tab) => {
            const isActive = tab.key === activeTab
            return (
              <button
                key={tab.key}
                type='button'
                role='tab'
                aria-selected={isActive}
                disabled={!tab.enabled}
                title={tab.enabled ? undefined : 'Coming soon'}
                onClick={() => switchTab(tab.key)}
                className={CN(
                  'flex items-center gap-2 px-4 py-3 -mb-px border-b-[3px] text-[14.5px] font-extrabold',
                  {
                    'border-[#F0A04B] text-[#2A2A2D]': isActive,
                    'border-transparent text-[#6B6F78]': !isActive,
                    'opacity-50 cursor-not-allowed': !tab.enabled,
                  },
                )}>
                {tab.label}
                {tab.key === 'validation' && pendingCount !== null && (
                  <span className='min-w-[20px] h-5 px-[6px] rounded-full bg-[#F0A04B] text-white text-xs font-extrabold inline-flex items-center justify-center'>
                    {pendingCount}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        <div className='flex items-center gap-3 flex-wrap'>
          {activeTab === 'all' && (
            <div className='flex items-center gap-2 text-[13.5px] font-bold text-[#3F3F46]'>
              <label htmlFor='sscc-xml-from'>From</label>
              <input
                id='sscc-xml-from'
                type='date'
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className={CN(
                  'h-10 w-[150px] border rounded-md px-[10px] text-sm text-[#2A2A2D] tabular-nums',
                  {
                    'border-[#D03A3A]': !rangeIsValid,
                    'border-[#DADBDF]': rangeIsValid,
                  },
                )}
              />
              <label htmlFor='sscc-xml-to'>To</label>
              <input
                id='sscc-xml-to'
                type='date'
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className={CN(
                  'h-10 w-[150px] border rounded-md px-[10px] text-sm text-[#2A2A2D] tabular-nums',
                  {
                    'border-[#D03A3A]': !rangeIsValid,
                    'border-[#DADBDF]': rangeIsValid,
                  },
                )}
              />
              {!rangeIsValid && (
                <span role='alert' className='text-xs font-bold text-[#D03A3A]'>
                  From cannot be later than To.
                </span>
              )}
            </div>
          )}
          <div className='flex-1' />
          <div className='flex items-center gap-2 w-[300px] h-10 border border-[#DADBDF] rounded-md px-3'>
            <i className='ri-search-line text-[#8A8F98]' />
            <input
              id='sscc-xml-search'
              aria-label='Search order number'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder='Search order number'
              className='flex-1 border-0 outline-none text-sm text-[#2A2A2D] bg-transparent'
            />
          </div>
          {activeTab === 'all' && (
            <button
              type='button'
              onClick={downloadCsv}
              disabled={!rangeIsValid}
              className='inline-flex items-center gap-2 h-10 px-4 rounded bg-[#F0A04B] text-white text-sm font-extrabold hover:bg-[#E8923A] disabled:opacity-50'>
              <i className='ri-download-2-line' />
              Download CSV
            </button>
          )}
        </div>

        {content}
      </div>

      {fixTarget && (
        <FixSsccXmlModal
          validationId={fixTarget.id}
          orderNumber={fixTarget.orderNumber}
          onClose={() => setFixTarget(null)}
          onSent={(fileName) => {
            setFixTarget(null)
            setRefreshKey((k) => k + 1)
            toast.success(`SSCC XML sent to T&W – ${fileName}`, {
              position: 'top-right',
              autoClose: 7000,
            })
          }}
        />
      )}
      <ToastContainer
        position='top-right'
        autoClose={5000}
        newestOnTop={false}
        closeOnClick
        pauseOnHover
        theme='colored'
        className='toast-container'
      />
    </div>
  )
}

export default SsccXmlValidationPage
